import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import BusinessIdentityStep from "@/components/onboarding/BusinessIdentityStep";
import { DEFAULT_SELECTED_WORKFLOWS } from "@/components/onboarding/WorkflowSelectionStep";
import ConnectorsStep from "@/components/onboarding/ConnectorsStep";
import ChannelConnectionsStep from "@/components/onboarding/ChannelConnectionsStep";
import ImportValueMetricsStep from "@/components/onboarding/ImportValueMetricsStep";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import * as Sentry from "@sentry/react";
import { ErrorFallback } from "@/components/ErrorFallback";
import OnboardingVisual from "@/components/onboarding/OnboardingVisual";
import { complimentaryTrialIssue } from "@/lib/trialAccess";

export interface OnboardingData {
  businessName: string;
  businessEmail: string;
  businessAddress: string;
  category: string;
  phone?: string;
  instagramUrl?: string;
  facebookUrl?: string;
  twitterUrl?: string;
  paymentMethodId?: string;
  listingName: string;
  listingDescription: string;
  totalCapacity: string;
  basePrice: string;
  images: string[];
  faqs: { question: string; answer: string }[];
  custom_questions: { id: string; question: string; type: "text" | "dropdown" | "radio"; required: boolean; options?: string[] }[];
  termsAndConditions: string;
  enableDynamicPricing: boolean;
  pricingRules: any[];
  schedule: { type?: 'recurring' | 'one_off'; weekly: { [day: string]: string[] }; exceptions: any[] };
  duration?: string;
  /** Automation keys retained for compatibility with older saved onboarding data. */
  selectedWorkflows: string[];
  selectedPlan: string;
  isYearly: boolean;
  enableDibsBookings: boolean;
}

const INITIAL_DATA: OnboardingData = {
  businessName: "",
  businessEmail: "",
  businessAddress: "",
  category: "",
  phone: "",
  instagramUrl: "",
  facebookUrl: "",
  twitterUrl: "",
  listingName: "",
  listingDescription: "",
  totalCapacity: "",
  basePrice: "",
  images: [],
  faqs: [],
  custom_questions: [],
  termsAndConditions: "",
  enableDynamicPricing: false,
  pricingRules: [],
  schedule: {
    type: "recurring",
    weekly: { monday: [], tuesday: [], wednesday: [], thursday: [], friday: [], saturday: [], sunday: [] },
    exceptions: [],
  },
  duration: "",
  selectedWorkflows: DEFAULT_SELECTED_WORKFLOWS,
  selectedPlan: "premium",
  isYearly: false,
  enableDibsBookings: false,
};

const Onboarding = () => {
  const [searchParams] = useSearchParams();
  // 4-slide flow: 1 Business · 2 Customers · 3 Connectors · 4 Value metrics.
  // The metrics slide is skipped when no customer data was imported. The old
  // plan/checkout slide no longer exists: every new business receives Premium
  // free for 14 days and completes onboarding from its last meaningful step.
  const rawInitialStep = parseInt(searchParams.get("step") || "1");
  const initialStep = Math.min(Math.max(rawInitialStep, 1), 4);
  const [step, setStep] = useState(initialStep);
  
  const [data, setData] = useState<OnboardingData>(() => {
    const saved = localStorage.getItem("dibs_onboarding_data");
    if (!saved) return INITIAL_DATA;
    // Merge against INITIAL_DATA so new fields (images, faqs, termsAndConditions)
    // get safe defaults when an older payload was cached before they existed.
    return { ...INITIAL_DATA, ...JSON.parse(saved) } as OnboardingData;
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { user, signOut, businessId } = useAuth();

  // Number of imported customers, reported up from the customer-import slide (and
  // fetched once on mount to cover deep-links). Drives whether the value-metrics
  // slide has anything real to show.
  const [contactCount, setContactCount] = useState<number | null>(null);

  // The value-metrics slide (step 4) is only meaningful with real imported data.
  // Today the only onboarding import path is contacts (the bookings import was
  // removed from this flow), so with nothing imported the slide would show a
  // "$0 sitting in revenue gaps" anticlimax at the end of setup. When there's
  // no data, we skip step 4 entirely and complete from the Connectors step.
  const hasMetricsData = (contactCount ?? 0) > 0;

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth", { replace: true });
  };

  useEffect(() => {
    localStorage.setItem("dibs_onboarding_data", JSON.stringify(data));
  }, [data]);

  // Seed the contact count on mount (covers deep-linking straight to a later
  // step). The customer-import slide keeps it fresh thereafter via onCountChange.
  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;
    supabase
      .from("customers")
      .select("customer_id", { count: "exact", head: true })
      .eq("business_id", businessId)
      .then(({ count, error }) => {
        if (error) {
          console.error("[onboarding] failed to load the imported customer count", error);
          return;
        }
        if (!cancelled) setContactCount(count ?? 0);
      });
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  const updateData = (fields: Partial<OnboardingData>) => {
    setData((prev) => ({ ...prev, ...fields }));
  };

  const nextStep = () => setStep((prev) => Math.min(prev + 1, 4));
  const prevStep = () =>
    setStep((prev) => {
      return Math.max(prev - 1, 1);
    });

  const totalVisibleSteps = hasMetricsData ? 4 : 3;
  const visibleStepNumber = Math.min(step, totalVisibleSteps);

  const submitOnboarding = async () => {
    if (!user) return;
    setIsSubmitting(true);
    try {
      // The database starts the complimentary Premium trial when this update
      // flips onboarding_complete to true. Billing dates are deliberately not
      // accepted from the browser.
      const { data: completedRows, error: bError } = await supabase.rpc(
        "complete_business_onboarding",
        {
          p_business_name: data.businessName,
          p_business_email: data.businessEmail,
          p_description: data.category,
          p_phone_number: data.phone || "",
          p_instagram_url: data.instagramUrl || "",
          p_facebook_url: data.facebookUrl || "",
          p_twitter_url: data.twitterUrl || "",
          p_temporary_metadata: {
            ...data,
            selectedPlan: "premium",
            isYearly: false,
          },
        },
      );
      if (bError) throw bError;
      const completedBusiness = completedRows?.[0];
      if (!completedBusiness) throw new Error("Business setup could not be verified.");

      const trialIssue = complimentaryTrialIssue({
        onboardingComplete: completedBusiness.onboarding_complete === true,
        subscriptionTier: completedBusiness.subscription_tier,
        subscriptionStatus: completedBusiness.subscription_status,
        stripeCustomerId: completedBusiness.stripe_customer_id,
        billingCycleAnchor: completedBusiness.billing_cycle_anchor,
        nextBillingDate: completedBusiness.next_billing_date,
      });
      if (trialIssue) {
        throw new Error(`Setup was saved, but the 14-day Premium trial could not be verified: ${trialIssue}.`);
      }

      toast.success("Your 14-day Premium trial is ready!");
      localStorage.removeItem("dibs_onboarding_data");
      navigate("/onboarding-success");
    } catch (error: any) {
      console.error("Failed to complete onboarding:", error);
      toast.error(error.message || "Something went wrong.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sentry.ErrorBoundary fallback={({ resetError }) => <ErrorFallback resetError={resetError} />} showDialog={false}>
      <div className="flex h-dvh max-h-dvh flex-1 flex-col overflow-hidden bg-background">
        {/* Fixed page progress, matching the reading-progress treatment on blog posts. */}
        <div className="fixed left-0 right-0 top-0 z-[60] h-1 bg-border/40 pointer-events-none">
          <div
            className="h-full w-full origin-left bg-primary transition-transform duration-300 ease-in-out"
            style={{ transform: `scaleX(${visibleStepNumber / totalVisibleSteps})` }}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round((visibleStepNumber / totalVisibleSteps) * 100)}
            aria-label="Onboarding progress"
          />
        </div>

        <div className="flex w-full shrink-0 items-center justify-between p-4 pt-5 md:p-6 md:pt-7">
          <div className="flex items-center gap-2" aria-label="Dibs">
            <img
              src="/dibs_assets/red_logo_with_transparent_bg.png"
              alt=""
              className="h-7 w-7 object-contain"
            />
            <span className="font-sans text-xl font-bold tracking-tight text-primary">Dibs</span>
          </div>
          <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={handleSignOut}>
            <LogOut className="w-4 h-4 mr-2" />
            Sign out
          </Button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center p-4 pb-8 md:-mt-5 md:px-6">
          <div className="grid h-full max-h-[760px] min-h-0 w-full max-w-6xl overflow-hidden rounded-xl border bg-card shadow-sm lg:grid-cols-[minmax(0,1.15fr)_minmax(340px,0.85fr)]">
            <main className="flex min-h-0 flex-col overflow-y-auto p-6 sm:p-8 lg:p-10">
              <div className="mb-8 shrink-0">
                <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-primary">
                  Step {visibleStepNumber} of {totalVisibleSteps}
                </p>
                <h1 className="text-2xl font-heading font-semibold tracking-tight">
                  {step === 1 && "Tell us about your business"}
                  {step === 2 && "Import your customers"}
                  {step === 3 && "Add connectors"}
                  {step === 4 && "Here's what Dibs can recover for you"}
                </h1>
                <p className="text-sm text-muted-foreground mt-2">
                  {step === 1 && "Let's set up your business profile."}
                  {step === 2 && "Import your customer data from a file or system."}
                  {step === 3 && "Connect the messaging channels and systems where your customers reach you."}
                  {step === 4 && "Based on the customer data you've imported so far."}
                </p>
              </div>

              <div className="flex min-h-0 flex-1 flex-col">
                {step === 1 && <BusinessIdentityStep data={data} updateData={updateData} nextStep={nextStep} />}
                {step === 2 && <ConnectorsStep nextStep={nextStep} prevStep={prevStep} onCountChange={setContactCount} />}
                {step === 3 && <ChannelConnectionsStep data={data} updateData={updateData} nextStep={hasMetricsData ? nextStep : submitOnboarding} prevStep={prevStep} isFinalStep={!hasMetricsData} isSubmitting={isSubmitting} />}
                {step === 4 && <ImportValueMetricsStep data={data} updateData={updateData} nextStep={submitOnboarding} prevStep={prevStep} isFinalStep isSubmitting={isSubmitting} />}
              </div>
            </main>

            <OnboardingVisual step={step} businessName={data.businessName} />
          </div>
        </div>
        <footer className="shrink-0 px-4 pb-4 text-center text-xs text-muted-foreground md:px-6 md:pb-5">
          © 2023 - 2026 Dibs Everything Pte Ltd. All rights reserved. | UEN: 202418462H
        </footer>
      </div>
    </Sentry.ErrorBoundary>
  );
};

export default Onboarding;
